/* eslint-disable no-debugger */
import { useEffect, useState } from "react";

import validate from "./MeetSanctionInfoFormValidationV2";

import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useMeetFileData from "@usas/ui-web/state/meetFile/UseMeetFileData";

import useUsasClubFacilityLinksData from '../../../../../state/clubFacilityLinks/UseUsasClubFacilityLinksData';
import useMeetFormatData from "../../../../../state/meetFormat/UseMeetFormatData";
import useMeetSanctionConfigurationData from "../../../../../state/meetSanctionConfiguration/UseMeetSanctionConfigurationData";

import Constants from "@usas/ui-web/utils/Constants";
import UseForm from "@usas/ui-web/utils/UseForm";

import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const SAVING_ERROR_MSG = 'The last action is still saving. Please try again.';
const FILE_TYPE_ERROR_MSG = 'File type not permitted. Please provide a (*.pdf), (*.doc), or (*.docx) file.';
const USAS_SANCTION_TEXT = 'This meet will be sanctioned through USA Swimming. By checking this box, you are agreeing to follow all USA Swimming sanctioning rules and guidelines.';
const LSC_SANCTION_TEXT = 'This meet will be sanctioned through your Local Swimming Committee (LSC). By checking this box, you are agreeing to follow all LSC sanctioning rules and guidelines. The meet will have a Pending status until it is approved by your LSC. Please check with your LSC for their sanctioning process and timelines.';

const UseMeetSanctionInfoFormV2 = (
  savedFormState, onSubmitFormCallback, state, setState, selectedMeetClassificationId, selectedMeetClassificationName, meetState, hasUsasSanctionAccess) => {
  const { formState, errorState, setIsDirty, handleSubmit, updateFormState, onValueTextPairChanged, setFormState
  } = UseForm(getInitialFormState, onSubmitFormCallback, validate);
  const { baseUsasClubState } = useBaseUsasClubData();
  const { usasClubFacilityLinksState, getUsasClubFacilityLinks } = useUsasClubFacilityLinksData();
  const [facilityOptionsState, setFacilityOptionsState] = useState([{ id: Constants.DEFAULT_ID, name: '--' }]);
  const { meetSanctionConfigurationState, getMeetSanctionConfiguration } = useMeetSanctionConfigurationData();


  const [removeFileModalState, setRemoveFileModalState] = useState(getInitialRemoveFileModalState());
  const { meetFileUploadState, postMeetFile, getMeetFile, getFileNameFromSavedUploadUrl } = useMeetFileData();
  const { meetFormatState, getMeetFormats } = useMeetFormatData();
  const [modalState, setModalState] = useState(getInitialModalState());
  const onOpenRemoveFileModalClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setRemoveFileModalState({ ...removeFileModalState, fileName: formState.meetAnnouncementFileName, displayPopUp: true });
  }

  const onRemoveFileModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setRemoveFileModalState({ ...getInitialRemoveFileModalState() });
  }

  const onUploadTemplateClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (meetFileUploadState.isObjLoading === true) {
      setState({ ...state, error: SAVING_ERROR_MSG });
    }
    else {
      setState({ ...state, error: '' });
      if (e.target.files[0]) {
        const file = e.target.files[0];
        const fileType = e.target.files[0].type;
        if (fileType === Constants.FILE_TYPE_PDF || fileType === Constants.FILE_TYPE_DOC ||
          fileType === Constants.FILE_TYPE_DOCX) {
          postMeetFile(file);
          setState({ ...state, error: '' });
        }
        else {
          setState({ ...state, error: FILE_TYPE_ERROR_MSG });
        }
      }
    }
  }

  const onRemoveTemplateClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setRemoveFileModalState({ ...getInitialRemoveFileModalState() });
    if (meetFileUploadState.isSaving === true) {
      setState({ ...state, error: SAVING_ERROR_MSG });
    }
    else {
      setState({ ...state, error: '' });
      setFormState({ ...formState, meetAnnouncementUrl: '', meetAnnouncementFileName: '' });
    }
  }

  const onDownloadUploadedFile = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(formState.meetAnnouncementUrl);
  }

  function checkFacilityPoolDepthRequirement(facilityId) {
    const facility = usasClubFacilityLinksState.arrayData.find(x => x.facilityId === facilityId);

    let requirementMet = false;

    if (meetState.isObjLoaded === true) {
      requirementMet = facility.facility.depthRequirementMet || meetState.objData.isInWaterStart;
    } else {
      requirementMet = facility.facility.depthRequirementMet || formState.isInWaterStart;
    }

    // return facility.facility.depthRequirementMet || formState.isInWaterStart;
    return requirementMet;
  }

  function getFacilityAsOptions(facilityArray) {
    const facilityOptions = [{ id: Constants.DEFAULT_ID, name: '--' }];
    if (facilityArray) {
      for (const facility of facilityArray) {
        if (facility.facility && facility.facility.hostMeet === true) {
          facilityOptions.push({ id: facility.facility.facilityId, name: facility.facility.facilityName });
        }
      }
    }
    return facilityOptions;
  }

  useEffect(() => {

    if (meetFormatState.isArrayLoading !== true && meetFormatState.isArrayLoaded !== true) {
      getMeetFormats();
    }

    setFormState({
      ...formState,
      ...savedFormState,
      autogeneratedMeetLsc: baseUsasClubState?.objData?.orgUnit?.parentOrgUnitCode || ''
    });
    setIsDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selMeetClassification = [];
    
    if (selectedMeetClassificationId > 0) {
      selMeetClassification.push({ id: selectedMeetClassificationId, name: selectedMeetClassificationName });
    }

    const facilityId = meetState.objData?.facilityId || savedFormState.facilityId || Constants.DEFAULT_ID;

    const selMeetCourse = [];
    if (meetState.objData?.meetCourse !== undefined) {
      selMeetCourse.push({ id: meetState.objData.meetCourse[0].course.courseId, name: meetState.objData.meetCourse[0].course.courseCode });
    }

    if (meetState.objData?.meetCourse !== undefined && meetState.objData.meetCourse.length > 1) {
      selMeetCourse.push({ id: meetState.objData.meetCourse[1].course.courseId, name: meetState.objData.meetCourse[1].course.courseCode });
    }

    if (meetState.objData?.meetCourse !== undefined && meetState.objData.meetCourse.length > 2) {
      selMeetCourse.push({ id: meetState.objData.meetCourse[2].course.courseId, name: meetState.objData.meetCourse[2].course.courseCode });
    }

    const meetSoftwareId = meetState.objData?.meetSoftwareId || savedFormState.meetSoftwareId || Constants.DEFAULT_ID;
    const meetFormatId = meetState.objData?.meetFormatId || savedFormState.meetFormatId || Constants.DEFAULT_ID;  
    const attestationSignature = savedFormState.attestationSignature || '';

    setFormState({
      ...formState,
      ...savedFormState,
      startDate: savedFormState.startDate ? formatDate(savedFormState.startDate) : Constants.BLANK_DATE_STRING,
      endDate: savedFormState.endDate ? formatDate(savedFormState.endDate) : Constants.BLANK_DATE_STRING,
      meetClassification: selMeetClassification,
      meetCourse: selMeetCourse,
      facilityId: facilityId,
      meetSoftwareId: meetSoftwareId,
      meetFormatId: meetFormatId,
      attestationSignature: attestationSignature,
      hasUsasSanctionAccess: hasUsasSanctionAccess
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFormState, selectedMeetClassificationId, selectedMeetClassificationName]);

  useEffect(() => {
    if (meetFileUploadState.isObjLoaded === true && Object.keys(meetFileUploadState.objData).length > 0) {
      setFormState({ ...formState, meetAnnouncementUrl: meetFileUploadState.objData.uploadUrl, meetAnnouncementFileName: getFileNameFromSavedUploadUrl() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetFileUploadState])

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoaded === false && usasClubFacilityLinksState.isArrayLoading === false) {
      getUsasClubFacilityLinks(baseUsasClubState.objData.usasClubId);

      getMeetSanctionConfiguration(6);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoading === false && usasClubFacilityLinksState.isArrayLoaded === true) {
      setFacilityOptionsState(getFacilityAsOptions(usasClubFacilityLinksState.arrayData || []));
    }
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  async function showUploadedFile(fileUrl) {
    if (fileUrl) {
      const getFileResult = await getMeetFile(fileUrl);

      if (getFileResult) {
        if (getFileResult.objData?.blobFile?.size > 0) {
          var file = window.URL.createObjectURL(getFileResult.objData?.blobFile);
          const link = document.createElement('a');

          link.href = file;
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        }
      }
    }
  }

  useEffect(() => {
    if (formState.facilityId > 0) {
      let metRequirement = false;
      metRequirement = checkFacilityPoolDepthRequirement(formState.facilityId);

      setModalState({
        ...modalState,
        displayModal: !metRequirement,
        facilityName: usasClubFacilityLinksState.arrayData.find(x => x.facilityId === formState.facilityId).facility.facilityName
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.facilityId]);

  function getInitialFormState() {
    return {
      meetName: '',
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING,
      meetClassification: [],
      meetClassificationId: Constants.DEFAULT_ID,
      meetClassificationName: '',
      meetCourse: [],
      meetSoftwareId: Constants.DEFAULT_ID,
      meetSoftwareName: '',
      facilityId: Constants.DEFAULT_ID,
      facilityName: '',
      autogeneratedMeetYear: '',
      autogeneratedMeetLsc: '',
      meetAnnouncementUrl: '',
      meetAnnouncementFileName: '',
      isUsasSanctionMeet: false,
      meetFormatId: Constants.DEFAULT_ID,
      meetFormatName: '',
      isInWaterStart: false,
      depthRequirementMet: false,
      descriptionOfMedicalSupervision: '',
      attestationSignature: '',
      meetSanctionAttestation: '',
      hasUsasSanctionAccess: false
    };
  }

  function getInitialRemoveFileModalState() {
    return {
      displayPopUp: false,
      fileName: '',
      modalTitle: 'Remove Uploaded File?'
    }
  }

  const onModalCancelClicked = () => {
    setModalState({
      ...getInitialModalState()
    });

    if (formState.depthRequirementMet === false && formState.isInWaterStart === false) {
      setFormState({
        ...formState, facilityId: Constants.DEFAULT_ID
      })
    }
  }

  function getInitialModalState() {
    return {
      displayModal: false,
      facilityName: ''
    }
  }

  return {
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged: updateFormState,
    onValueTextPairChanged,
    onUploadTemplateClicked,
    onOpenRemoveFileModalClicked,
    onRemoveFileModalCanceled,
    onRemoveTemplateClicked,
    meetFileUploadState,
    removeFileModalState,
    meetFormatState,
    modalState,
    meetSanctionConfigurationState,
    onDownloadUploadedFile,
    onModalCancelClicked,
    USAS_SANCTION_TEXT,
    LSC_SANCTION_TEXT
  };
};

export default UseMeetSanctionInfoFormV2;